import React from 'react';
import T from 'prop-types';
// redux
import { connect } from 'react-redux';
import { setCategoryAllPass } from 'reducers/siteVisitReport';
import { openModal } from 'reducers/modals';
// components
import { FormattedMessage } from 'react-intl';
import ConfirmationModal from 'components/ConfirmationModal/ConfirmationModal';
// utils
import classnames from 'classnames/bind';
import mixpanel from 'mixpanel-browser';
import { toastResponseErrors } from 'utils/responseErrorsHelper';
import {
  isSVRCategoryAllPassed,
  hasSVRCategoryFailedAnswers,
  isSVRQuestionBulkPassable,
  isSVRQuestionComplete,
} from 'utils/SVRHelper';
// styles
import styles from './CategoryRollUp.module.scss';

const cn = classnames.bind(styles);

// Category-level "all good" confirmation: yes passes every question in the category at once,
// no reveals the individual questions for entry
const CategoryRollUp = ({
  category, categoryIndex, currentUser, farmId, reportId, isDraft, categoryPassAll, areQuestionsVisible,
  setCategoryAllPass, openModal, onConfirm, onDecline,
}) => {
  const passableQuestions = category.svr_questions.filter(isSVRQuestionBulkPassable);
  if (!isDraft || !categoryPassAll || !passableQuestions.length) return null;

  // Highlight "no" only for real problems - failed answers; N/A and open answers are neutral.
  // A category holding a fail is never "all good", so the highlights stay mutually exclusive
  const isDeclined = hasSVRCategoryFailedAnswers(category);
  const isAllPassed = !isDeclined && isSVRCategoryAllPassed(category);
  // Answers the bulk pass would overwrite: non-pass values and follow-up flags
  const overwriteCount = passableQuestions.filter(
    ({ answer }) => (answer?.value && answer.value !== 'pass') || answer?.is_flagged
  ).length;

  const trackRollUpAction = (action, extra = {}) => {
    if (currentUser && currentUser.id) {
      mixpanel.track('Feature Used', {
        user_id: currentUser.id,
        feature_name: 'CategoryRollUp',
        action,
        category_name: category.name,
        category_index: categoryIndex,
        farm_id: farmId,
        report_id: reportId,
        timestamp: new Date().toISOString(),
        ...extra,
      });
    }
  };

  const passCategory = () => {
    trackRollUpAction('Pass Category', {
      questions_passed: passableQuestions.length,
      answers_overwritten: overwriteCount,
    });
    const hasRemainingQuestions = category.svr_questions.some(
      (question) => !isSVRQuestionBulkPassable(question) && !isSVRQuestionComplete(question)
    );
    // Collapse only after answers are actually saved
    setCategoryAllPass()
      .then(() => onConfirm(hasRemainingQuestions))
      .catch(toastResponseErrors);
  };

  const onYes = () => {
    // Overwriting existing answers is destructive - confirm it; a clean category passes instantly
    if (overwriteCount) {
      openModal(
        <ConfirmationModal
          title={(
            <FormattedMessage
              id="component.svrCategoryRollUp.confirmTitle"
              values={{ category: category.name }}
            />
          )}
          actionBtnProps={{ primary: true }}
          handleConfirm={passCategory}
        >
          <FormattedMessage
            id="component.svrCategoryRollUp.overwriteWarning"
            values={{ count: overwriteCount }}
          />
        </ConfirmationModal>
      );
      return;
    }
    passCategory();
  };

  const onNo = () => {
    trackRollUpAction('Reveal Category Questions');
    onDecline();
  };

  return (
    <div className={cn('category-roll-up')}>
      <span className={cn('question-text')}>
        <FormattedMessage id="component.svrCategoryRollUp.question" values={{ category: category.name }} />
      </span>
      <div className={cn('btn-group')}>
        <div onClick={onYes} className={cn('btn', 'yes', { selected: isAllPassed })}>
          <FormattedMessage id="component.svrCategoryRollUp.confirm" />
        </div>
        {/* No only reveals the hidden questions - once they are visible it has nothing to do */}
        {!areQuestionsVisible && (
          <div onClick={onNo} className={cn('btn', 'no', { selected: isDeclined })}>
            <FormattedMessage id="general.button.no" />
          </div>
        )}
      </div>
    </div>
  );
};

CategoryRollUp.propTypes = {
  category: T.object.isRequired,
  categoryIndex: T.number.isRequired,
  setCategoryAllPass: T.func.isRequired,
  openModal: T.func.isRequired,
  onConfirm: T.func.isRequired,
  onDecline: T.func.isRequired,
  areQuestionsVisible: T.bool,
  currentUser: T.object,
  farmId: T.oneOfType([T.string, T.number]),
  reportId: T.oneOfType([T.string, T.number]),
  isDraft: T.bool,
  categoryPassAll: T.bool,
};

export default connect(
  (state) => ({
    currentUser: state.auth.user,
    farmId: state.siteVisitReport.farm?.id,
    reportId: state.siteVisitReport.report?.id,
    isDraft: state.siteVisitReport.report?.isDraft,
    categoryPassAll: state.siteVisitReport.report?.category_pass_all,
  }), (dispatch, { categoryIndex }) => ({
    setCategoryAllPass: () => dispatch(setCategoryAllPass(categoryIndex)),
    openModal: (component) => dispatch(openModal(component)),
  })
)(CategoryRollUp);
